<template>
  <section>
    <div class="goal-heading" :class="{ 'mt-40': goalIndex !== 0 }">
      <!-- Edit/Delete Dropdown-->
      <Dropdown v-if="isTeamLead" class="edit-dropdown" menu-right>
        <button type="button" class="edit-button" data-test="goal-edit-button" data-role="trigger">
          <img src="../../assets/goal-capture/edit-pencil.svg" alt="Edit" />
        </button>
        <template #dropdown>
          <li v-if="locked">
            <a
              role="button"
              tabindex="0"
              @click="handleEditClick"
              @keyup.enter="handleEditClick"
              data-test="edit-goal-option"
              >Edit</a
            >
          </li>
          <li v-if="locked" role="separator" class="divider"></li>
          <li>
            <a
              role="button"
              tabindex="0"
              @click="handleDeleteClick"
              @keyup.enter="handleDeleteClick"
              data-test="delete-goal-option"
              >Delete</a
            >
          </li>
        </template>
      </Dropdown>

      <h3>🎯 Goal {{ goalIndex + 1 }}</h3>
      <div class="goal-input-container">
        <GoalCaptureBox
          @update:goalName="updateGoalName"
          :text="goalName"
          :label="`team goal ${goalIndex}`"
          :goal-number="goalIndex"
          :locked="locked || !isTeamLead"
        />
      </div>
    </div>
    <div v-if="hasEnteredInput && !locked" class="mt-40">
      <GoalSuggestionBox
        :goal-input="goalName"
        :goal-index="goalIndex"
        :prompt="suggestionPrompt"
        @update:goalName="updateGoalName"
      />
      <p class="goal-doc-paragraph mt-40">Once you’re happy with this goal, capture it below.</p>
      <div class="center-flex">
        <button
          class="btn btn-primary"
          type="button"
          @click.prevent="handleGoalSubmit"
          data-test="submit-goal-button"
        >
          Capture Goal
        </button>
      </div>
    </div>

    <div v-else-if="hasEnteredInput && locked" class="mt-40 locked-analyses-container">
      <div v-for="analysis in props.cmsAnalyses" :key="analysis.type">
        <!--        <p class="goal-doc-paragraph center-flex">{{ analysis.name }}:</p>-->
        <GoalAnalysis
          v-if="
            (isTeamLead && goal.createdById === csContext.user.id) ||
            !!getAnalysis(analysis.type)?.analysis
          "
          :goal-id="goal.id"
          :goal-input="goalName"
          :prompt="analysis.smart_callout.data.prompt"
          :analysis="getAnalysis(analysis.type)"
          :cms-analysis-type="analysis.type"
          @update:analysis="updateAnalysis"
        />
        <div v-else class="analysis-not-available-container">
          This analysis is still being generated for your Team Lead. Once finished, you’ll be able
          to see it here. Refresh the page to check.
        </div>
      </div>
    </div>

    <Modal v-if="showModal" v-model="showModal" :header="false" :footer="false">
      <div class="capture-goal-modal">
        <h2>🎯 Capture Goal?</h2>
        <img src="../../assets/goal-capture/open-quote.svg" alt="Open Quote" />
        <p>{{ goalName }}</p>
        <img src="../../assets/goal-capture/close-quote.svg" alt="Close Quote" />
        <span class="modal-subtext"
          >Note: You will be able to edit your goals after capturing them</span
        >
        <div style="margin-bottom: 10px">
          <button class="btn btn-default" @click="closeModal" type="button">Cancel</button>
          <button
            class="btn btn-primary"
            @click="captureGoal"
            type="button"
            style="margin-left: 20px"
            data-test="capture-goal-modal-button"
          >
            Capture Goal
          </button>
        </div>
      </div>
    </Modal>
  </section>
</template>
<script setup lang="ts">
import { computed, ref, inject, Ref } from "vue";
import { Modal, Dropdown } from "uiv";
import axios, { AxiosError } from "axios";
import GoalCaptureBox from "./goal-capture-box.vue";
import { TeamGoal } from "../../views/cms/cms-goal-capture-page.vue";
import GoalAnalysis from "./goal-analysis.vue";
import GoalSuggestionBox from "./goal-suggestion-box.vue";
import { CmsGoalAnalysis } from "../../services/cms/cms-type-definitions";
import { csContext } from "../../services/cs-context";
import { TeamGoalAnalysisApiModel } from "../../api-models";
import goalCaptureStore from "../../stores/goal-capture-store";

const isTeamLead = inject("isTeamAdmin") as Ref<boolean>;

const props = defineProps<{
  goalIndex: number;
  goal: TeamGoal;
  cmsAnalyses: CmsGoalAnalysis[];
  suggestionPrompt: string;
}>();

const goalName = ref(props.goal.goalName);
const showModal = ref(false);
const error = ref("");
const analyses = ref<TeamGoalAnalysisApiModel[]>(props.goal.teamGoalAnalyses);

const locked = computed(() => props.goal.locked);

const hasEnteredInput = computed(() => goalName.value.length > 0);

const emit = defineEmits(["update:lockStatus", "deleteGoal", "update:goal"]);

function updateGoalName(newValue: string) {
  goalName.value = newValue;
}

function closeModal() {
  showModal.value = false;
}

function handleGoalSubmit() {
  showModal.value = true;
}

function handleEditClick() {
  emit("update:lockStatus", props.goalIndex, false);
}

function getAnalysis(type: string): TeamGoalAnalysisApiModel | undefined {
  return analyses.value.find((a) => a.analysisType === type);
}

function handleDeleteClick() {
  emit("deleteGoal", props.goalIndex);
  goalCaptureStore.deleteSuggestionsForGoal(props.goalIndex);
}

async function saveNewGoal() {
  const { data: id } = await axios.post<string>("/api/teamgoals", {
    teamId: csContext.currentTeam?.id,
    goalName: goalName.value,
  });

  return id;
}

async function saveEditedGoal() {
  await axios.put("/api/teamgoals", {
    id: props.goal.id,
    teamId: csContext.currentTeam?.id,
    goalName: goalName.value,
  });
}

async function updateAnalysis(analysis: string, analysisType: string) {
  const index = analyses.value.findIndex((a) => a.analysisType === analysisType);

  if (index === -1) {
    analyses.value.push({ analysis, analysisType });
  } else {
    analyses.value[index].analysis = analysis;
  }
}

async function captureGoal() {
  try {
    let id = "";
    if (props.goal.id) {
      await saveEditedGoal();
      analyses.value.forEach((a) => updateAnalysis("", a.analysisType));
    } else {
      id = await saveNewGoal();
    }

    emit("update:goal", props.goalIndex, {
      id: id || props.goal.id,
      goalName: goalName.value,
      locked: true,
      createdById: csContext.user.id,
    });
    closeModal();
  } catch (e: any) {
    error.value = (e as AxiosError).message;
  }
}
</script>
<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

@smart-text-box-offset: 40px;
@border-radius: 5px;

.goal-heading {
  height: 120px;
  background-color: @charismatic-color;
  margin: 20px auto 0;
  text-align: center;
  padding: 20px;
  position: relative;
  border-radius: @border-radius;
  color: @body-bg;

  h3 {
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  .goal-input-container {
    position: absolute;
    width: 80%;
    bottom: -@smart-text-box-offset;
    left: 50%;
    transform: translateX(-50%);
  }
}

.analysis {
  width: clamp(280px, 80%, 600px);
  margin: calc(@smart-text-box-offset + 20px) auto 0;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: @off-white;
  border-radius: @border-radius;

  P {
    margin: 0;
  }
}

.mt-40 {
  margin-top: calc(@smart-text-box-offset + 20px);
}

.capture-goal-modal {
  display: flex;
  text-align: center;
  gap: 20px;
  align-items: center;
  flex-direction: column;

  p {
    margin: -5px 0;
  }

  img {
    width: 10px;
  }

  button {
    padding: 6px 18px;
  }
}

.modal-subtext {
  margin-top: 20px;
  font-size: 12px;
  color: @form-text-color;
}

.edit-dropdown {
  position: absolute;
  top: 20px;
  right: 20px;

  .divider {
    height: 1px;
    margin: 1px 0;
  }

  li > a {
    color: @form-text-color;
  }
}

.edit-button {
  background-color: transparent;
  border: none;

  &:hover {
    img {
      filter: brightness(0.9);
    }
  }
}

.locked-analyses-container {
  margin-bottom: -40px;
}

.analysis-not-available-container {
  background-color: @off-white;
  border-radius: 5px;
  margin-bottom: 10px;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
}
</style>
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.edit-dropdown {
  .dropdown-menu {
    border-radius: 10px;
    min-width: 0;

    a {
      border-radius: 4px;
    }
  }
}

.goal-doc-paragraph {
  color: @form-text-color;
  font-size: 16px;
  margin-bottom: 20px;
}
</style>
